import { type Nullable, type SnakeCaseKeys } from '@Types';
import { type PlaceDetails } from 'GooglePlaces';
import { type LearnerProject } from 'LearnerProjects';

export enum MemberDetailsMembershipStatus {
    CREATOR = 'creator',
    INVITED = 'invited',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
}

export enum ProjectGroupMembershipStatus {
    INVITED = 'invited',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
    REMOVED = 'removed',
}

type ProjectGroupMembershipBase = {
    id: string;
    createdAt: string;
    updatedAt: string;
    invitedByUserId: string;
    isCreator: boolean;
    projectGroupId: string;
    requirementIdentifier: string;
    respondedAt: Nullable<string>;
    status: ProjectGroupMembershipStatus;
    userId: string;
    // FIXME: user isn't 100% correct because the user attrs are different in the admin case.
    user?: ProjectGroupMemberUserAttrs;
};

// Project groups are included in the user like this, so sometimes
// they have project_group_memberships and sometimes not
// user = {
//     project_group_memberships: [
//         {
//             project_group: {
//                 project_group_memberships: [
//                     {
//                         project_group: undefined
//                     }
//                 ]
//             }
//         }
//     ]
// }
type ProjectGroupMembership<Options extends { HasProjectGroup: boolean }> = ProjectGroupMembershipBase &
    Options['HasProjectGroup'] extends true
    ? ProjectGroupMembershipBase & { projectGroup: ProjectGroupWithMembershipsAndProject }
    : ProjectGroupMembershipBase;

export type ProjectGroupMembershipWithProjectGroup = ProjectGroupMembership<{ HasProjectGroup: true }>;
export type ProjectGroupMembershipSansProjectGroup = ProjectGroupMembership<{ HasProjectGroup: false }>;

export type ProjectGroupMembershipSnakeCasedAttrs<Options extends { HasProjectGroup: boolean }> = SnakeCaseKeys<
    ProjectGroupMembership<Options>
>;

export type ProjectGroupSansMembershipsAndProject = {
    id: string;
    createdAt: string;
    updatedAt: string;
    acceptedMemberCount: number;
    cohortId: string;
    invitedMemberships: boolean;
    requirementIdentifier: string;
};

export type ProjectGroupWithMemberships = ProjectGroupSansMembershipsAndProject & {
    // When memberships are embedded in a project_group, the memberships themselves don't embed the project_group,
    // because that would create a circular reference.
    memberships: ProjectGroupMembershipSansProjectGroup[];
};

export type ProjectGroupWithMembershipsAndProject = ProjectGroupWithMemberships & {
    project: LearnerProject;
};

export type MemberDetails = {
    id: string;
    email: string;
    avatarUrl: Nullable<string>;
    name: string;
    jobTitle: Nullable<string>;
    location: string;
    detailsStatus: Nullable<MemberDetailsMembershipStatus>;
    membershipStatus: Nullable<ProjectGroupMembershipStatus>;
};

export enum CreateProjectGroupModalPages {
    MEMBER_SEARCH = 'member_search',
    REVIEW_AND_CONFIRM = 'review_and_confirm',
    SUCCESS = 'success',
}

export type ProjectGroupMemberUserAttrs = {
    id: string;
    email: string;
    name: string;
    avatarUrl: Nullable<string>;
    city: string;
    state: string;
    country: string;
    careerProfileFeaturedWorkExperience: Nullable<{
        id: string;
        jobTitle: string;
        professionalOrganization: {
            id: string;
            imageUrl: Nullable<string>;
            locale: string;
            name: string;
        };
    }>;
    careerProfilePlaceDetails: PlaceDetails;
};

// ProjectGroupsApi

export type CreateProjectGroupParams = {
    record: {
        cohort_id: string;
        requirement_identifier: string;
        member_emails: string[];
    };
};

export type ProjectGroupMemberSearchParams = {
    search: string;
    cohort_id: string;
    requirement_identifier: string;
};

export type CreateProjectGroupResponse = {
    contents: {
        projectGroups: ProjectGroupSansMembershipsAndProject[];
    };
    meta: {
        pushMessages: {
            currentUser: Record<string, unknown>;
        };
    };
};

export type ProjectGroupShowResponse = {
    contents: { projectGroups: ProjectGroupWithMemberships[] };
};

// ProjectGroupMembershipsApi

export type ProjectGroupMembershipApiResponse = {
    contents: {
        projectGroupMemberships: ProjectGroupMembership<{ HasProjectGroup: false }>[];
    };
    meta: {
        pushMessages: {
            currentUser: Record<string, unknown>;
        };
    };
};
